import { captureMessage } from '@sentry/react';
import { IPayload } from '@estee/elc-universal-utils';
import { HttpTransport, IResponse } from '@estee/elc-http-transport';

import { HttpMethod } from '~api/CartRestAPI/utils/utils';
import { diContainer } from '~setup/diContainer';
import { serviceNames } from '~setup/CartService';

import {
    parseTotals,
    reduceToCartItems,
    reduceToCartOrderData,
    VULCAN_CART_SCHEMA
} from './utils/utils';
import { IRawVulcanCartData, IVulcanResponse } from './utils/IVulcanResponse';

const httpTransport = new HttpTransport();

export const makeVulcanRequest = async <R>(
    method: HttpMethod,
    headers?: {
        [header: string]: string;
    },
    body?: IPayload['body']
) => {
    try {
        const response = await httpTransport[method]({ url: '/cart/v3_0/page', headers, body });

        return response as IResponse<R>;
    } catch (error) {
        captureMessage('Rest API [makeRestAPIRequest] - call failed', {
            level: 'warning',
            tags: { api: true, custom: true },
            extra: { error }
        });

        return {} as IResponse<R>;
    }
};

export const fetchVulcanCart = async () => {
    const response = await makeVulcanRequest<IRawVulcanCartData>(HttpMethod.GET);

    const cartItems = reduceToCartItems(response);
    const cartTotals = parseTotals(response);
    const cartOrderData = reduceToCartOrderData(response);

    return { cartItems, cartTotals, cartOrderData } as IVulcanResponse;
};

export const fetchVulcanCartWithSchema = async () => {
    try {
        const csrf_token =
            document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';

        const headers = { 'content-type': 'application/json', 'x-csrf-token': csrf_token };
        const response = await makeVulcanRequest<IVulcanResponse>(
            HttpMethod.POST,
            headers,
            JSON.stringify({
                schema: VULCAN_CART_SCHEMA
            })
        );
        const { data } = response;

        return (data ?? {}) as IVulcanResponse;
    } catch {
        return fetchVulcanCart();
    }
};

export const isCartVulcanAPI = () => {
    const { config } = diContainer.get(serviceNames.config);

    return config.serverCartEnabled as 'enabled' | 'enabled:schema' | boolean;
};
